/*
 * <rla-carousel-wheel> - the homepage product carousel's rotary navigator
 * (sections/rla-product-carousel.liquid, `navigator: wheel`).
 *
 * A SEPARATE ELEMENT from <rla-stamp-wheel>, deliberately. They share the ring
 * GEOMETRY (assets/rla-stamp-wheel.css, one file, both sections load it) and
 * share nothing else, because they are answering different questions:
 *
 *   rla-stamp-wheel   the wheel IS the content. One .rlsw-panel visible at a
 *                     time, so it needs a shortest-path angle accumulator, a
 *                     tablist, and a one-time entrance spin.
 *   rla-carousel-wheel  the wheel is only NAVIGATION. The cards beside it are
 *                     the content, they are all visible at once, and the ring's
 *                     angle is a pure function of how far the track is scrolled.
 *
 * Extending the other file was tried first and rejected: under the design below
 * the accumulator, the transitionend filters, windUp(), settle() and
 * parkOffset() are all dead weight, so "sharing" would have meant inheriting
 * ~120 lines this element never runs, from a file that drives a live page.
 *
 * THE SYNC, AND WHY THERE IS NO GUARD FLAG.
 * Rotating scrolls the track, which fires scroll, which would rotate the wheel
 * again. Rather than suppress that with a flag whose lifetime has to outlive
 * `scroll-behavior: smooth` (whose duration is distance-dependent, so a timer is
 * always wrong), the data flow is made one-directional:
 *
 *     scroll position  ->  ring angle          ALWAYS, and only ever this way
 *     click / key / drag  ->  scroll position   never touches the angle
 *
 *     angle = -(scrollLeft / itemOffset) * step
 *
 * One step of rotation per card of scroll, continuous, no quantisation. The read
 * path never writes scroll and the write path never writes rotation, so there is
 * no loop to break.
 *
 * A QUANTISED VERSION WAS TRIED AND IS WRONG AT THE BOUNDARY. With 10 cards and
 * 2 visible the last card that can ever LEAD is index 8, so "scroll to card i,
 * then read back the leading card" is not an identity for i = 9: the track hits
 * max scroll, the read returns 8, and the wheel visibly bounces back one stamp.
 * Clamping the offsets only turns the bounce into a stamp that can never be
 * selected. Continuously, scrollLeft = max simply maps to -8.x steps and the
 * ring rests between the last two stamps, which is the truth - for FREE
 * scrolling. A deliberate SELECTION is the one amendment:
 *
 * THE ROTATIONAL BIAS. Selecting a tail stamp whose offset exceeds maxScroll
 * (index 9 here) is an intent the track cannot express in pixels, so the
 * shortfall becomes a bias in card units and the angle is
 *
 *     angle = -((scrollLeft / itemOffset) + bias) * step
 *
 * The bias is TWEENED by the same glide that moves the track, never derived
 * from scrollLeft: indices 8 and 9 share one scroll position, so a
 * scroll-derived ramp would have zero frames to animate 8 <-> 9 over and
 * degenerate to the 36deg snap it was meant to remove. The flow stays
 * one-directional - scroll (plus the glide-owned bias) -> angle; input ->
 * scroll - and when manual ownership begins (a real drag, wheel or touch on
 * the track, or the distance fallback in sync()) the selection is released
 * and any bias is SHED linearly over one card of the user's own travel, so
 * clearing it never snaps the ring.
 *
 * Reduced motion: the ENTRANCE is dropped and programmatic scrolls become
 * instant, but the ring still tracks. A navigator that refuses to move while the
 * thing it indexes is moving is not calmer, it is lying. This is coupled motion
 * driven by the user's own scroll, the same category as a scrollbar thumb.
 */
(function () {
  'use strict';

  if (typeof HTMLElement === 'undefined') return;
  if (customElements.get('rla-carousel-wheel')) return;

  function motionReduced() {
    if (window.theme && window.theme.config && window.theme.config.motionReduced) return true;
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Wrap into (-180, 180] so a drag across the 0/360 seam does not spin. */
  function wrapDeg(d) {
    d = ((d % 360) + 360) % 360;
    return d > 180 ? d - 360 : d;
  }

  class RlaCarouselWheel extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;
      var self = this;
      /* The track is a sibling section-level element and slider-element inits
         itself lazily; one frame of slack is enough and avoids a layout read
         during the section's own first paint. */
      requestAnimationFrame(function () {
        if (self.isConnected) self.enhance();
      });
    }

    disconnectedCallback() {
      /* Theme editor re-mounts on shopify:section:load. Every listener below is
         bound to a node OUTSIDE this element (the track, the window), so unlike
         a self-contained element these genuinely leak if not removed: the old
         handlers keep firing against detached cards forever. */
      if (this.__track && this.__onScroll) this.__track.removeEventListener('scroll', this.__onScroll);
      if (this.__track && this.__onManual) {
        this.__track.removeEventListener('wheel', this.__onManual);
        this.__track.removeEventListener('pointerdown', this.__onManual);
      }
      if (this.__onResize) window.removeEventListener('resize', this.__onResize);
      if (this.__onWinUp) {
        window.removeEventListener('pointerup', this.__onWinUp);
        window.removeEventListener('pointercancel', this.__onWinUp);
      }
      if (this.__raf) cancelAnimationFrame(this.__raf);
      if (this.__glide) cancelAnimationFrame(this.__glide);
      this.__glide = 0;
      if (this.__hubT) clearTimeout(this.__hubT);
      this.__raf = 0;
      this.__init = false;
    }

    enhance() {
      this.ring = this.querySelector('[data-ring]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('.rlsw-tab'));
      this.status = this.querySelector('[data-rlsw-status]');
      /* The center hub readout. Optional by design: everything below no-ops
         without it, so the element keeps working if the hub is removed. */
      this.hub = this.querySelector('[data-rlsw-hub]');
      this.hubName = this.hub ? this.hub.querySelector('[data-hub-name]') : null;
      this.hubHook = this.hub ? this.hub.querySelector('[data-hub-hook]') : null;
      this.hubLink = this.hub ? this.hub.querySelector('[data-hub-link]') : null;
      if (!this.ring || this.tabs.length < 2) return;

      var trackId = this.getAttribute('data-rsw-for');
      this.__track = trackId ? document.getElementById(trackId) : null;
      if (!this.__track) return;

      this.cards = Array.prototype.slice.call(this.__track.querySelectorAll('.card-grid > .rla-cc-card'));
      if (!this.cards.length) return;

      this.step = parseFloat(this.dataset.step) || 360 / this.tabs.length;
      this.index = -1;

      /* Sticky-selection state. __sel records a deliberate activation's
         intent; __rotBias is the clamp overhang currently painted into the
         ring, in cards (see the header); the __shed* trio unwinds a stale
         bias across manual travel instead of zeroing it in one frame. */
      this.__sel = null;
      this.__rotBias = 0;
      this.__shedding = false;
      this.__shedFrom = 0;
      this.__shedTravel = 0;
      this.__shedLast = 0;

      var self = this;
      this.measure();

      this.__onScroll = function () {
        if (self.__raf) return;
        self.__raf = requestAnimationFrame(function () {
          self.__raf = 0;
          self.sync();
        });
      };
      this.__track.addEventListener('scroll', this.__onScroll, { passive: true });

      /* Manual input on the TRACK itself (trackpad wheel, touch pan, most
         scrollbar grabs) begins manual ownership at the gesture, not two
         pixels into it; the distance fallback in sync() stays as the backstop
         for input these never see. Passive: they only touch bookkeeping, and
         deliberately never repaint - until real movement arrives nothing on
         screen should change. */
      this.__onManual = function (e) {
        /* A vertical wheel over this horizontal-only track scrolls the PAGE,
           not the track, yet still bubbles here; taking manual ownership on
           it would abort an in-flight glide and drop the selection while
           scrollLeft never moves. Only horizontal-dominant wheel input is a
           gesture on the track. Pointerdown carries no axis and passes. */
        if (e.type === 'wheel' && !(Math.abs(e.deltaX) > Math.abs(e.deltaY))) return;
        self.beginManualRotation();
      };
      this.__track.addEventListener('wheel', this.__onManual, { passive: true });
      this.__track.addEventListener('pointerdown', this.__onManual, { passive: true });

      this.__onResize = function () {
        var wasGliding = !!self.__glide;
        self.measure();
        /* Fresh geometry can change which offsets clamp, so a carried bias is
           stale by definition. Mid-glide, re-run the journey against the new
           numbers; at rest, re-derive the overhang (or zero it) outright - a
           resize reflows everything, so a discrete correction is invisible. */
        self.__shedding = false;
        if (wasGliding) {
          if (self.__sel != null) {
            self.goTo(self.__sel, false);
            return;
          }
          self.cancelGlide();
        }
        self.__rotBias = self.__sel != null ? self.overhang(self.__sel) : 0;
        self.sync();
      };
      window.addEventListener('resize', this.__onResize, { passive: true });

      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function (e) {
          /* Modified clicks and middle clicks keep the link's own meaning, the
             same fallthrough the seal rail already ships. */
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          self.goTo(i, true);
        });
        tab.addEventListener('keydown', function (e) {
          self.onKey(e, i);
        });
      });

      this.bindDrag();
      this.sync();
      this.classList.add('is-enhanced');
    }

    /*
     * itemOffset is the card pitch (card width + grid gap), and offsets[i] is
     * the scrollLeft that puts card i at the track's leading edge. Both are read
     * ONCE per layout rather than per scroll frame, so the scroll handler does
     * no layout work at all.
     */
    measure() {
      var t = this.__track;
      var base = this.cards[0].getBoundingClientRect().left;
      /* base and each card rect come from the SAME layout pass, so their
         difference is already scroll-independent; adding scrollLeft on top
         (the first version did) double-counts it and skews every offset by
         however far the track happens to be scrolled when a resize triggers
         a re-measure. */
      this.offsets = this.cards.map(function (c) {
        return Math.round(c.getBoundingClientRect().left - base);
      });
      this.itemOffset =
        this.cards.length > 1 ? this.offsets[1] - this.offsets[0] : this.cards[0].getBoundingClientRect().width;
      if (!this.itemOffset) this.itemOffset = 1;
      this.maxScroll = Math.max(0, t.scrollWidth - t.clientWidth);
    }

    /* scroll position -> ring angle. The ONLY writer of --wheel-rot. */
    sync() {
      /* display:none (below the wheel breakpoint) has no offsetParent; skip the
         work rather than write transforms nothing can paint. */
      if (!this.offsetParent) return;
      var t = this.__track;
      var pos = t.scrollLeft / this.itemOffset;

      /* aria-current is quantised separately: it is a discrete claim about which
         product leads, and it must not churn on every sub-pixel of scroll. */
      var first = Math.max(0, Math.min(this.tabs.length - 1, Math.round(pos)));
      var lead = first;

      /* STICKY SELECTION, and it exists because of the clamp boundary: with 10
         cards and 2 visible, offsets[9] clamps to maxScroll = offsets[8], so a
         click on the LAST stamp used to scroll the track and then mark the
         PREVIOUS product current - "I clicked Resin Bloom and Peak Bloom showed
         up". A deliberate activation records its intent; while the track sits
         at the closest position it can reach for that intent, the intent wins
         over the leading-card read. Scrolling away by hand releases it (but not
         mid-glide, when divergence is just the journey) - releases, not zeroes:
         beginManualRotation() arms the shed below so the ring unwinds instead
         of snapping. Validated BEFORE the rotation write so a frame can never
         paint a selection this same pass releases. */
      if (this.__sel != null && this.offsets[this.__sel] != null) {
        var selTarget = Math.max(0, Math.min(this.maxScroll, this.offsets[this.__sel]));
        if (Math.abs(t.scrollLeft - selTarget) <= 2) lead = this.__sel;
        else if (!this.__glide) this.beginManualRotation();
      }

      /* Shed a released bias across ACCUMULATED manual travel (absolute
         deltas, so a wiggle still sheds and scrolling back to maxScroll can
         never re-pin an unselected stamp), gone within one card of movement. */
      if (this.__shedding) {
        this.__shedTravel += Math.abs(t.scrollLeft - this.__shedLast);
        this.__shedLast = t.scrollLeft;
        var remaining = Math.max(0, 1 - this.__shedTravel / this.itemOffset);
        this.__rotBias = this.__shedFrom * remaining;
        if (!remaining) this.__shedding = false;
      }

      this.ring.style.setProperty('--wheel-rot', -((pos + this.__rotBias) * this.step).toFixed(3) + 'deg');

      if (lead === this.index) return;
      var firstSync = this.index < 0;
      this.index = lead;
      /* Rides the quantised lead gate above, so the hub swaps once per card
         change, never per scroll pixel. Liquid already rendered product 0, so
         the first sync writes without the dip. */
      this.updateCenter(lead, firstSync);

      /* How many cards are on screen decides how many stamps read as "in view".
         Two are visible in wheel mode, but derive it rather than assume it, so a
         columns change cannot make the highlight lie. The in-view band is
         anchored on the VISUALLY first card, not the selection, so at the
         boundary the on-screen neighbour still reads as on screen. */
      var perView = Math.max(1, Math.round(t.clientWidth / this.itemOffset));
      this.tabs.forEach(function (tab, j) {
        var on = j === lead;
        var near = !on && j >= first && j < first + perView;
        tab.classList.toggle('is-active', on);
        tab.classList.toggle('is-inview', near);
        if (on) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });
    }

    /*
     * The center hub readout: name, hook and link of the LEADING product. Not
     * a live region on purpose (the sr-only status element announces, and only
     * on deliberate activation) - this is the visual voice of the selection.
     * The 150ms dip is skipped on the first paint (Liquid already rendered
     * product 0) and under reduced motion.
     */
    updateCenter(lead, immediate) {
      if (!this.hub || !this.hubName) return;
      var tab = this.tabs[lead];
      if (!tab) return;
      var self = this;
      var write = function () {
        self.hubName.textContent = tab.getAttribute('data-title') || '';
        if (self.hubHook) self.hubHook.textContent = tab.getAttribute('data-hook') || '';
        if (self.hubLink) self.hubLink.setAttribute('href', tab.getAttribute('href') || '#');
      };
      if (immediate || motionReduced()) {
        /* An immediate write must also retire any in-flight dip, or the
           pending timer later overwrites this content with its stale tab. */
        clearTimeout(this.__hubT);
        this.hub.classList.remove('is-swapping');
        write();
        return;
      }
      this.hub.classList.add('is-swapping');
      clearTimeout(this.__hubT);
      this.__hubT = setTimeout(function () {
        write();
        self.hub.classList.remove('is-swapping');
      }, 150);
    }

    /* Cards of scroll that card i's leading-edge target loses to the clamp.
       Non-zero only at the tail, where offsets[i] exceeds maxScroll. */
    overhang(i) {
      var target = Math.max(0, Math.min(this.maxScroll, this.offsets[i]));
      return Math.max(0, (this.offsets[i] - target) / this.itemOffset);
    }

    /* Manual ownership begins: the user is moving the track themselves, so a
       recorded selection no longer speaks for them. Releasing it must not
       also snap the ring, so a nonzero bias is handed to the shed in sync()
       and unwinds inside the motion the user is already making. Deliberately
       does NOT call sync(): a gesture that produces no actual scroll (an
       overscroll flick at maxScroll, a tap on a card) must change nothing on
       screen. */
    beginManualRotation() {
      this.cancelGlide();
      this.__sel = null;
      /* A trackpad gesture is a STREAM of wheel events. Re-arming on each one
         would restart the shed from the already-partially-shed bias with
         travel zeroed, turning the linear decay multiplicative (the residual
         after distance D becomes ~exp(-D/itemOffset), which never reaches 0
         and rests the ring visibly rotated off scroll truth). One gesture,
         one shed: let an in-flight shed finish. */
      if (this.__shedding) return;
      if (this.__rotBias) {
        this.__shedding = true;
        this.__shedFrom = this.__rotBias;
        this.__shedTravel = 0;
        this.__shedLast = this.__track.scrollLeft;
      }
    }

    /* Everything that is not a scroll writes HERE, and only to scroll. */
    goTo(i, announce) {
      /* Record the intent for the sticky-selection read in sync(): the clamp
         boundary means the track cannot always LEAD with card i, but the
         activation was still FOR product i. A fresh intent also retires any
         shed in progress; the glide tweens the bias from wherever it is. */
      this.__sel = i;
      this.__shedding = false;
      var target = Math.max(0, Math.min(this.maxScroll, this.offsets[i]));
      var bias = this.overhang(i);
      /* 'instant', not 'auto': behavior 'auto' defers to the track's CSS
         scroll-behavior, which is smooth, so 'auto' is NOT instant here. */
      if (motionReduced()) {
        this.__track.scrollTo({ left: target, behavior: 'instant' });
        this.__rotBias = bias;
      } else {
        this.glideTo(target, bias);
      }
      /* Two clamped stamps share one scroll position (offsets[9] and [8] both
         clamp to maxScroll), so selecting the second produces NO scroll event
         and no sync would run: force one so the selection reads back. */
      this.sync();
      /* Announce only on a discrete, deliberate activation. Wiring this to the
         scroll path would make a screen reader recite the whole line on one
         flick of the track. */
      if (announce && this.status) {
        this.status.textContent = this.tabs[i].getAttribute('data-title') || '';
      }
    }

    /*
     * Own tween instead of scrollTo({behavior:'smooth'}), because the native
     * animation's duration grows with distance: measured on the shipped
     * build, an opposite-side stamp (5 cards, ~1760px) took 874ms of scroll
     * and the ring trailed to 1603ms behind its 700ms ease. Selecting far
     * should not cost seconds. The duration is distance-AWARE but capped -
     * one card ~290ms, the far side ~620ms - with an ease-in-out so arrival
     * still reads as settling, not stopping dead.
     *
     * The tween owns scrollLeft for its duration, so the same two theme
     * behaviors that fight per-frame writes during a drag are suspended via
     * the same class mechanism (scroll-snap re-snaps, scroll-behavior:smooth
     * turns every assignment into its own animation). If anything else moves
     * the track mid-glide (trackpad, scrollbar), the deviation check hands
     * control straight over rather than fighting the user.
     */
    glideTo(target, targetBias) {
      var self = this;
      var t = this.__track;
      this.cancelGlide();
      var from = t.scrollLeft;
      var dist = target - from;
      var biasFrom = this.__rotBias;
      var biasDist = (targetBias || 0) - biasFrom;
      /* 8 <-> 9 travel ZERO scroll pixels but a full card of bias, so bail
         only when both the track and the bias are already there. */
      if (Math.abs(dist) < 1 && Math.abs(biasDist) < 0.001) return;
      var cards = Math.abs(dist) / this.itemOffset + Math.abs(biasDist);
      var dur = Math.max(280, Math.min(620, 200 + 90 * cards));
      t.classList.add('rla-cc-track--gliding');
      var t0 = performance.now();
      var last = from;
      function ease(p) {
        return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      }
      function step(now) {
        if (Math.abs(t.scrollLeft - last) > 2) {
          /* someone else is scrolling; theirs wins - and the handoff must
             also release the selection, or its bias would pin the ring
             against the user's own motion. */
          self.beginManualRotation();
          return;
        }
        var p = Math.min(1, (now - t0) / dur);
        var ep = ease(p);
        last = from + dist * ep;
        t.scrollLeft = last;
        /* The bias rides the same clock and the same ease as the scroll, so
           the ring's sweep is ONE continuous curve however the journey splits
           between pixels and overhang. A bias-only glide produces no scroll
           events, so sync() is driven from here; on mixed glides the track's
           own rAF-coalesced scroll sync doubles it harmlessly. */
        self.__rotBias = biasFrom + biasDist * ep;
        self.sync();
        if (p < 1) self.__glide = requestAnimationFrame(step);
        else self.cancelGlide();
      }
      this.__glide = requestAnimationFrame(step);
    }

    cancelGlide() {
      if (this.__glide) cancelAnimationFrame(this.__glide);
      this.__glide = 0;
      if (this.__track) this.__track.classList.remove('rla-cc-track--gliding');
    }

    onKey(e, i) {
      var last = this.tabs.length - 1;
      var next = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      if (next < 0) return;
      e.preventDefault();
      this.tabs[next].focus();
      this.goTo(next, true);
    }

    /*
     * Pointer drag, as a three-phase state machine: idle -> pending -> active.
     *
     * THE PHASES EXIST BECAUSE OF A POINTER-CAPTURE TRAP that shipped and cost
     * every stamp its click: setPointerCapture on pointerdown redirects the
     * ensuing `click` to the CAPTURING element (Pointer Events spec), so the
     * click's target was the ring, never the <a> stamp, and the per-tab click
     * handlers were unreachable by any real mouse or finger. A press is
     * therefore only a press until the pointer has actually travelled:
     * capture, the drag classes and the click suppressor all engage at the
     * activation threshold, never on contact.
     *
     * The threshold is PIXELS, not degrees: angleAt() is an atan2 about the
     * ring center, so near the hub a small wiggle sweeps a huge angle and a
     * degree threshold would misfire.
     *
     * Drag writes SCROLL, never rotation, so it goes through the same one-way
     * path as everything else and the ring follows for free. An angle sweep of d
     * degrees is (d / step) cards, hence (d / step) * itemOffset pixels; the sign
     * is negative because the ring rotates negatively as the track advances.
     */
    bindDrag() {
      var self = this;
      var IDLE = 0,
        PENDING = 1,
        ACTIVE = 2;
      var phase = IDLE;
      var startX = 0;
      var startY = 0;
      var lastAngle = 0;
      var accum = 0;
      var startScroll = 0;
      var pid = null;
      var suppressClick = false;

      function angleAt(e) {
        var r = self.ring.getBoundingClientRect();
        return (Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180) / Math.PI;
      }

      this.ring.addEventListener('pointerdown', function (e) {
        if (e.button !== 0 || !self.offsetParent) return;
        /* Round 3 fix F18: below the section's 1023px container query the
           ring is a static two-column CHIP GRID, not a wheel (the section
           sets position: static and transform: none on it). A rotary drag
           there has no centre to measure an angle from, so a horizontal
           finger drag across the chips wrote nonsense to the card track's
           scrollLeft. No wheel geometry, no drag: taps still select. */
        if (getComputedStyle(self.ring).position === 'static') return;
        /* One gesture owns the wheel: a second concurrent pointer (a resting
           thumb) must not hijack pid/phase mid-drag and strand the classes. */
        if (phase !== IDLE) return;
        /* The hub is a sibling of the ring, so its events never arrive here;
           this bail is insurance against a future markup reshuffle. */
        if (e.target.closest && e.target.closest('.rlsw-center')) return;
        /* Grabbing a gliding wheel stops it, like a physical one. */
        self.cancelGlide();
        phase = PENDING;
        suppressClick = false;
        startX = e.clientX;
        startY = e.clientY;
        lastAngle = angleAt(e);
        pid = e.pointerId;
      });

      this.ring.addEventListener('pointermove', function (e) {
        if (phase === IDLE) return;
        if (pid !== null && e.pointerId !== pid) return;
        /* Button released out of our sight: pre-capture, the ring gets no
           pointerup once the pointer has left it, so a buttonless move here
           means the gesture already ended elsewhere. */
        if ((e.buttons & 1) === 0) {
          end(false);
          return;
        }
        if (phase === PENDING) {
          if (Math.hypot(e.clientX - startX, e.clientY - startY) <= 8) return;
          phase = ACTIVE;
          suppressClick = true;
          /* Manual ownership: a live selection stops speaking for the user
             the moment a REAL drag begins - never on contact, so a clean tap
             leaves it for the click's own goTo to replace. */
          self.beginManualRotation();
          /* Snap and smooth easing both fight per-frame scrollLeft writes; the
             classes go on at activation, never on contact, so a clean tap
             never flips the cursor or disables the track's snap. */
          self.classList.add('is-dragging');
          self.__track.classList.add('rla-cc-track--dragging');
          /* Rebase: discard the pre-threshold sweep and read the LIVE scroll
             position, which also makes a press during an in-flight smooth
             scroll pick up from where the track actually is. */
          accum = 0;
          lastAngle = angleAt(e);
          startScroll = self.__track.scrollLeft;
          try {
            self.ring.setPointerCapture(pid);
          } catch (err) {
            /* capture is a nicety; the move handler still works without it */
          }
          return;
        }
        var a = angleAt(e);
        var d = wrapDeg(a - lastAngle);
        lastAngle = a;
        accum += d;
        var next = startScroll - (accum / self.step) * self.itemOffset;
        self.__track.scrollLeft = Math.max(0, Math.min(self.maxScroll, next));
      });

      function end(cancelled) {
        if (phase === IDLE) return;
        var wasActive = phase === ACTIVE;
        phase = IDLE;
        /* A pending end() is a TOTAL no-op, and that carries two fixes: a
           tap's end no longer races the click's own goTo with a snap of its
           own, and a vertical touch pan (claimed by the browser under
           touch-action: pan-y, delivered as pointercancel before the
           threshold) no longer yanks the track sideways. */
        if (wasActive) {
          self.classList.remove('is-dragging');
          self.__track.classList.remove('rla-cc-track--dragging');
          if (pid !== null) {
            try {
              self.ring.releasePointerCapture(pid);
            } catch (err) {
              /* already released */
            }
          }
          if (cancelled) {
            /* The browser claimed an ACTIVE gesture (a vertical touch pan
               that crossed the threshold before pointercancel arrived). The
               sideways frames already written were accidental, so put the
               track back where the gesture found it - and clear the latch,
               because a cancelled pointer never produces the click that
               would consume it, and a stale latch would eat the next
               keyboard Enter on a stamp. */
            suppressClick = false;
            self.__track.scrollLeft = Math.max(0, Math.min(self.maxScroll, startScroll));
          } else {
            /* Settle onto the nearest card so a drag ends where a click would. */
            var i = Math.max(0, Math.min(self.tabs.length - 1, Math.round(self.__track.scrollLeft / self.itemOffset)));
            self.goTo(i, false);
          }
        }
        pid = null;
      }

      function onPointerEnd(e) {
        if (pid !== null && e.pointerId !== pid) return;
        end(e.type === 'pointercancel');
      }

      this.ring.addEventListener('pointerup', onPointerEnd);
      this.ring.addEventListener('pointercancel', onPointerEnd);
      /* Backstop: capture can fail (the try/catch above), and then a release
         outside the ring never reaches it, leaving the drag classes and the
         disabled snap stuck. Window sees every release; a duplicate call is
         a no-op because end() bails on IDLE. Removed in disconnectedCallback. */
      this.__onWinUp = onPointerEnd;
      window.addEventListener('pointerup', onPointerEnd);
      window.addEventListener('pointercancel', onPointerEnd);

      /* A drag that ends on a stamp must not also follow that stamp's link.
         Capture phase, so this runs before the per-tab click handler. (While
         capture was held the click targets the ring anyway; this layer covers
         a capture that failed.) */
      this.ring.addEventListener(
        'click',
        function (e) {
          if (suppressClick) {
            e.preventDefault();
            e.stopPropagation();
            suppressClick = false;
          }
        },
        true
      );
    }
  }

  customElements.define('rla-carousel-wheel', RlaCarouselWheel);
})();
